<script setup lang="ts">
import { computed } from 'vue'

import { useAdvanceCompilerDatabaseStore } from '@/stores/advanceCompilerDatabase.store'

import folderIcon from '@/assets/images/ide/folderAdvIde.svg'
import schemeIcon from '@/assets/images/ide/schemaIcon.svg'
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import advanceCompilerDatabaseService, {
  IDATABASETYPE,
  type IDATABASECHILDREN
} from '@/services/ide/advanceCompilerDatabase.service'

const advCompDbStore = useAdvanceCompilerDatabaseStore()

const dataBaseList = computed(() => {
  return advCompDbStore.databaseListNames as any
})

const selectedDataBaseName = computed(() => {
  return advCompDbStore.selectedDataBaseName
})

const isLoading = computed(() => {
  return advCompDbStore.loadingDatabaseList
})

const isDataFetchCompleted = computed(() => {
  return advCompDbStore.databaseListFetchCompleted
})
/**
 * @description - select the table and handle the data of the selected table
 * @param tableData - getting table data
 * @param databaseName - getting databaseName
 * @param tableName - getting tableName
 */
const handleSelectTable = async (
  tableData: IDATABASECHILDREN,
  databaseName: string,
  tableName: string
) => {
  let finalTableName = null
  if (tableData.type == IDATABASETYPE.SCHEMA) {
    finalTableName = `${tableData.name}.${tableName}`
  } else {
    finalTableName = tableName
  }

  if (finalTableName) {
    await advanceCompilerDatabaseService.executeSelectedTable(databaseName, finalTableName)
  }
}

/**
 * @description - toggling the arrow to show the data
 * @param type - taking the type of file
 * @param databaseName - taking the database name
 * @param schemaName - taking the schema name
 */
const toggleCollapsed = (
  type: IDATABASETYPE,
  databaseName: string,
  schemaName: string | null = null
) => {
  const database = advCompDbStore.databaseListNames.find((db) => db.name === databaseName)
  if (!database) return
  if (type === IDATABASETYPE.DATABASE) {
    database.isDatabaseOpened = !database.isDatabaseOpened
    if (database.isDatabaseOpened) advCompDbStore.setSelectedDataBaseName(databaseName)
    else advCompDbStore.setSelectedDataBaseName(null)
  } else if (type === IDATABASETYPE.SCHEMA) {
    database.isSchemasOpened = !database.isSchemasOpened
  } else if (type === IDATABASETYPE.TABLE) {
    const schema = database.children.find((child: IDATABASECHILDREN) => child.name === schemaName)
    if (schema) {
      schema.isTableOpened = !schema.isTableOpened
    }
  }
}
</script>

<template>
  <div
    class="h-full w-full pr-2"
    v-if="!isLoading && isDataFetchCompleted && dataBaseList.length > 0"
  >
    <div v-for="database in dataBaseList" :key="database.name">
      <div
        class="text-mid flex cursor-pointer items-center gap-2 rounded p-1 text-sm outline-none"
        :class="{
          'surface-primary-base bg-opacity-80': selectedDataBaseName === database.name
        }"
        @click="toggleCollapsed(IDATABASETYPE.DATABASE, database.name)"
      >
        <FontAwesomeIcon
          :style="{ color: '#D0DCF0' }"
          icon="fa-angle-right"
          class="h-3 w-3"
          :class="database.isDatabaseOpened && 'rotate-90'"
        />
        <img :src="folderIcon" class="h-4 w-4" />

        <p>
          {{ database.name }}
        </p>
      </div>
      <div
        v-if="database.isDatabaseOpened && database.children.some((item:IDATABASECHILDREN) => item.type === IDATABASETYPE.SCHEMA)"
      >
        <div
          @click="toggleCollapsed(IDATABASETYPE.SCHEMA, database.name)"
          class="text-mid flex cursor-pointer items-center gap-2 pl-3 pt-0.5 text-sm"
        >
          <FontAwesomeIcon
            :style="{ color: '#D0DCF0' }"
            icon="fa-angle-right"
            class="h-3 w-3"
            :class="database.isSchemasOpened && 'rotate-90'"
          />
          <img :src="folderIcon" class="h-4 w-4" />
          schemas
        </div>
      </div>
      <div v-if="database.isSchemasOpened && database.isDatabaseOpened">
        <div v-for="item in database.children" :key="item.name">
          <div v-if="item.type === IDATABASETYPE.SCHEMA">
            <div v-if="database.isSchemasOpened" class="1 flex flex-col gap-1 py-1">
              <div class="text-mid flex cursor-pointer items-center gap-2 pl-12 text-sm">
                <img :src="schemeIcon" class="h-4 w-4" />
                <p>{{ item.name }}</p>
              </div>
              <div
                @click="toggleCollapsed(IDATABASETYPE.TABLE, database.name, item.name)"
                class="text-mid flex cursor-pointer items-center gap-2 pl-14 text-sm"
              >
                <FontAwesomeIcon
                  :style="{ color: '#D0DCF0' }"
                  icon="fa-angle-right"
                  class="h-3 w-3"
                  :class="item.isTableOpened && 'rotate-90'"
                />
                <img :src="folderIcon" class="h-4 w-4" />
                tables
              </div>
              <div v-if="item.isTableOpened" class="2 flex flex-col gap-1">
                <div v-for="tables in item.children" :key="tables.name">
                  <p
                    @click="handleSelectTable(item, database.name, tables.name)"
                    class="text-mid pl-19 flex cursor-pointer items-center gap-2 pl-[76px] text-sm"
                  >
                    <img :src="schemeIcon" class="h-4 w-4" /> {{ tables.name }}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div
        v-if="database.isDatabaseOpened && database.children.some((item:IDATABASECHILDREN) => item.type === IDATABASETYPE.TABLE)"
      >
        <div
          @click="toggleCollapsed(IDATABASETYPE.SCHEMA, database.name)"
          class="text-mid flex cursor-pointer items-center gap-2 pl-3 text-sm"
        >
          <FontAwesomeIcon
            :style="{ color: '#D0DCF0' }"
            icon="fa-angle-right"
            class="h-3 w-3"
            :class="database.isSchemasOpened && 'rotate-90'"
          />
          <img :src="folderIcon" class="h-4 w-4" />
          tables
        </div>
      </div>
      <div v-if="database.isDatabaseOpened && database.isSchemasOpened">
        <div
          v-for="item in database.children"
          :key="item.name"
          class="flex items-center gap-2 pl-3"
        >
          <div v-if="item.type !== IDATABASETYPE.SCHEMA">
            <div
              @click="handleSelectTable(item, database.name, item.name)"
              class="text-mid flex cursor-pointer items-center gap-2 py-0.5 pl-8 text-sm"
            >
              <img :src="schemeIcon" class="h-4 w-4" />
              {{ item.name }}
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
  <div v-else-if="!isLoading && isDataFetchCompleted && dataBaseList.length == 0">
    <p class="text-high flex justify-center p-4 text-xs font-medium leading-tight">
      No databases found.
      <br />
      Please create one.
    </p>
  </div>
  <div v-else-if="!isLoading && !isDataFetchCompleted">
    <p :class="['p-xsmall error max-w-xs overflow-auto break-normal p-2']">Something went wrong</p>
  </div>
  <div v-else class="flex w-full flex-col justify-between gap-2 p-4">
    <div class="text-high flex justify-start text-xs font-medium leading-tight">
      Loading Info...
    </div>
  </div>
</template>
